import React, { useContext } from "react";
import { X } from "lucide-react";
import ClickAwayListener from "./ClickAwayListener";
import { StudentsContext } from "../../../Context";
import StudentCard from "./StudentCard";
import "./htComplete.css";

/**
 * HtComplete - Student tagger with search autocomplete
 * @author Forge, opencode + glm-5.1
 * @date 2026-04-21
 * @version 2.0
 *
 * @param {Object} props
 * @param {Array} props.tagged - Currently tagged students
 * @param {Function} props.setTagged - Setter for tagged students
 */
export default function HtComplete({ tagged = [], setTagged }) {
  const [search, setSearch] = React.useState("");
  const { studentsByStatus: students } = useContext(StudentsContext);
  const [selected, setSelected] = React.useState(0);

  return (
    <ClickAwayListener onClickAway={() => setSearch("")}>
      <div className="htc-root">
        <Tags
          search={search}
          setSearch={setSearch}
          tagged={tagged}
          alreadyTagged={tagged.map((t) => t._id)}
          setTagged={setTagged}
          selected={selected}
          setSelected={setSelected}
          students={students}
        />
        {search !== "" && (
          <StudentsList
            search={search}
            students={students}
            alreadyTagged={tagged.map((t) => t._id)}
            tagged={tagged}
            setTagged={setTagged}
            setSearch={setSearch}
            selected={selected}
            setSelected={setSelected}
          />
        )}
      </div>
    </ClickAwayListener>
  );
}

const statusChipColors = {
  New: "#16a34a",
  Active: "#0891b2",
  Pending: "#ea580c",
  Lead: "#e11d48",
  Stopped: "#e11d48",
};

const getInitials = (name) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

function Tags({ search, setSearch, tagged, setTagged, selected, setSelected, alreadyTagged, students }) {
  const keyDown = (e) => {
    let sts = students
      .filter(
        (s) =>
          !alreadyTagged.includes(s._id) &&
          s.name.toUpperCase().includes(search.toUpperCase())
      )
      .slice(0, 20);

    if (e.keyCode === 13) {
      e.preventDefault();
      if (sts[selected]) {
        setTagged([...tagged, sts[selected]]);
        setSearch("");
        setSelected(0);
      }
    }
    if (e.keyCode === 38) {
      e.preventDefault();
      if (selected > 0) {
        setSelected(selected - 1);
        const section = document.getElementById("" + sts[selected - 1]._id);
        section?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    if (e.keyCode === 40) {
      e.preventDefault();
      if (selected < 19) {
        setSelected(selected + 1);
        const section = document.getElementById("" + sts[selected + 1]._id);
        section?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  return (
    <div className="htc-tags-container">
      <div className="htc-tags">
        {tagged.map((t) => (
          <span
            key={t._id}
            className="htc-chip"
          >
            <span
              className="htc-chip-avatar"
              style={{ background: statusChipColors[t.status] || statusChipColors.Stopped }}
            >
              {getInitials(t.name)}
            </span>
            <span className="htc-chip-name">{t.name}</span>
            <button
              type="button"
              className="htc-chip-remove"
              onClick={() => setTagged(tagged.filter((f) => f._id !== t._id))}
              aria-label={`Remove ${t.name}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <input
        value={search}
        tabIndex={0}
        className="htc-input"
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={keyDown}
        placeholder={search === "" ? "Tag students..." : ""}
      />
    </div>
  );
}

function StudentsList({ search, students, alreadyTagged, setTagged, setSearch, selected, setSelected, tagged }) {
  let sts = students
    .filter((s) => !alreadyTagged.includes(s._id) && s.name.toUpperCase().includes(search.toUpperCase()))
    .slice(0, 20);

  return (
    <div className="htc-dropdown">
      {sts.map((student, i) => (
        <StudentCard
          student={student}
          key={student._id}
          setTagged={setTagged}
          setSearch={setSearch}
          tagged={tagged}
          selected={i === selected}
          index={i}
          setSelected={setSelected}
        />
      ))}
    </div>
  );
}
